import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { isNil } from 'lodash';
import { StatusIndicator } from 'tuui';
import { useCampaignGroupPickerSorting } from 'components/AppBreadcrumbs/hooks/useCampaignGroupPickerSorting';
import { useDimension } from '../../../../hooks';
import { PARAMS_TO_KEEP } from '../../../../modules/campaigns/components/CampaignPickerListItem/CampaignPickerListItem';
import { ENTITY, getCampaignStatusType, getStatusIndicatorType } from '../../../../modules/campaigns/config';
import { AllEntityId } from '../../../../modules/campaigns/config/allEntityId';
import { useSelectedCampaignGroup } from '../../../../modules/campaigns/hooks/useSelectedCampaignGroup';
import { fetchCampaignGroupsList } from '../../../../modules/campaigns/modules/campaigns-group/flows';
import { useAccount } from '../../../../modules/taboola-common-frontend-modules/account-configurations';
import { FormattedMessage } from '../../../../modules/taboola-common-frontend-modules/i18n';
import { useBreadcrumbNavigation } from '../../hooks/useBreadcrumbNavigation';
import { usePickerTotal } from '../../hooks/usePickerTotal';
import { BreadcrumbsDropdown } from '../BreadcrumbsDropdown/BreadcrumbsDropdown';
import { BreadcrumbsSearchComponent } from '../BreadcrumbsDropdown/components/BreadcrumbsSearchComponent/BreadcrumbsSearchComponent';
import { CampaignGroupSortComponent } from '../CampaignGroupSortComponent/CampaignGroupSortComponent';
import DropdownCampaignEditButton from '../DropdownCampaignEditButton/DropdownCampaignEditButton';
import { DropdownOptionRenderer } from '../DropdownOptionRenderer/DropdownOptionRenderer';
import commonStyles from '../commonPickerDropdownStyle.module.scss';

const CAMPAIGN_GROUP_PICKER_KEY = 'CAMPAIGN_GROUP_PICKER_KEY';

const CAMPAIGN_GROUP_DATA_AUTOMATION_ID = 'breadcrumbs-campaign-group-picker';
const CAMPAIGN_GROUP_QUERY_KEY_PREFIX = 'campaign-group-picker';

const OptionRenderer = props => {
    const {
        data: { id, status },
    } = props;
    const statusType = getCampaignStatusType(status);
    const statusIndicatorType = getStatusIndicatorType(statusType);

    return (
        <DropdownOptionRenderer
            {...props}
            idPropName="id"
            namePropName="name"
            entity={ENTITY.CAMPAIGNS_GROUP}
            editButtonComponent={DropdownCampaignEditButton}
            icon={statusIndicatorType && id !== AllEntityId ? <StatusIndicator type={statusIndicatorType} /> : <span />}
        />
    );
};

export const BreadcrumbsCampaignGroupPicker = ({ mainContainerWidth }) => {
    const dispatch = useDispatch();
    const [dimension] = useDimension();
    const account = useAccount();
    const { accountName } = account;
    const { campaignGroup: cachedCampaignGroup } = useSelectedCampaignGroup();
    const { sortGetter } = useCampaignGroupPickerSorting();

    const fetcher = useCallback(
        async ({ searchText, pageSize, page, queryKey }) => {
            const [result, total] = await dispatch(
                fetchCampaignGroupsList({
                    accountName,
                    page,
                    pageSize,
                    searchText,
                    sort: sortGetter(),
                    apiCacheKey: queryKey,
                })
            );

            if (isNil(total)) {
                return { items: [], total: 0 };
            }

            return { items: result, total };
        },
        [accountName, dispatch, sortGetter]
    );

    const totalCount = usePickerTotal({
        fetcher,
        enabled: cachedCampaignGroup?.campaignsGroupId === AllEntityId && !isNil(account.accountName),
        queryKey: [CAMPAIGN_GROUP_PICKER_KEY, account?.accountName, dimension],
    });

    const allOptionsValue = isNil(cachedCampaignGroup?.campaignsGroupId)
        ? null
        : {
              id: AllEntityId,
              name: <FormattedMessage id="app.breadcrumbs.campaign.group.all" values={{ count: '' }} />,
              hideId: true,
          };

    const campaignGroupValue =
        isNil(cachedCampaignGroup?.campaignsGroupId) || cachedCampaignGroup.campaignsGroupId === AllEntityId ? (
            <FormattedMessage
                id="app.breadcrumbs.campaign.group.all"
                defaultMessage="All Campaign Groups"
                values={{ count: isNil(totalCount) ? '' : `(${totalCount})` }}
            />
        ) : (
            cachedCampaignGroup.campaignsGroupName
        );

    const selectedValueObject = useMemo(
        () => ({ ...cachedCampaignGroup, id: cachedCampaignGroup?.campaignsGroupId }),
        [cachedCampaignGroup]
    );

    const handleSelectedOption = useBreadcrumbNavigation({
        entityType: ENTITY.CAMPAIGNS_GROUP,
        paramsToKeep: PARAMS_TO_KEEP,
        selectedItem: selectedValueObject,
    });

    const alignmentTrigger =
        account?.accountName + cachedCampaignGroup?.campaignsGroupId + dimension + mainContainerWidth;
    return (
        <BreadcrumbsDropdown
            label={<FormattedMessage id="app.breadcrumbs.campaign.group" className={commonStyles['label']} />}
            value={campaignGroupValue}
            allOption={allOptionsValue}
            fetcher={fetcher}
            onSelect={handleSelectedOption}
            ItemComponent={OptionRenderer}
            SortComponent={CampaignGroupSortComponent}
            SearchComponent={BreadcrumbsSearchComponent}
            selectedItem={selectedValueObject}
            alignmentTrigger={alignmentTrigger}
            dataAutomationId={CAMPAIGN_GROUP_DATA_AUTOMATION_ID}
            queryKey={[CAMPAIGN_GROUP_QUERY_KEY_PREFIX, account?.accountName, dimension, JSON.stringify(sortGetter())]}
            enabled={!isNil(accountName)}
        />
    );
};
